import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Clock, MapPin, Users, Check, X, CalendarDays } from "lucide-react";

const VN_DAYS = ["Thứ 2", "Thứ 3", "Thứ 4", "Thứ 5", "Thứ 6", "Thứ 7", "Chủ nhật"];

function startOfWeek(d) {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7; // Mon=0
  x.setDate(x.getDate() - day);
  x.setHours(0, 0, 0, 0);
  return x;
}
function isoDate(d) { return d.toISOString().slice(0, 10); }

export default function StaffSchedule() {
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));
  const [data, setData] = useState({ days: [] });
  const [branches, setBranches] = useState([]);
  const [branchFilter, setBranchFilter] = useState("__all__");
  const [busy, setBusy] = useState(null);

  const fromIso = isoDate(weekStart);
  const toIso = isoDate(new Date(weekStart.getTime() + 6 * 86400000));

  const load = useCallback(async () => {
    const params = new URLSearchParams({ from_date: fromIso, to_date: toIso });
    if (branchFilter !== "__all__") params.set("branch_id", branchFilter);
    const r = await api.get(`/shifts/calendar?${params}`);
    setData(r.data);
  }, [branchFilter, fromIso, toIso]);
  useEffect(() => { api.get("/branches").then((r) => setBranches(r.data)); }, []);
  useEffect(() => { load(); }, [load]);

  const register = async (templateId, workDate) => {
    setBusy(`${templateId}_${workDate}`);
    try {
      await api.post("/shifts/register", { template_id: templateId, work_date: workDate });
      toast.success("Đã đăng ký ca ✓");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Đăng ký không thành công");
    } finally { setBusy(null); }
  };

  const cancelMine = async (regId) => {
    if (!window.confirm("Hủy đăng ký ca này?")) return;
    setBusy(regId);
    try {
      await api.delete(`/shifts/registrations/${regId}`);
      toast.success("Đã hủy");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Không hủy được");
    } finally { setBusy(null); }
  };

  const shiftWeek = (n) => setWeekStart(new Date(weekStart.getTime() + n * 7 * 86400000));

  return (
    <div data-testid="page-staff-schedule" className="pb-4">
      <div className="bg-gradient-to-br from-emerald-900 to-emerald-800 text-white px-4 pt-4 pb-5">
        <div className="text-[10px] uppercase tracking-[0.2em] text-amber-300 font-bold mb-1">Lịch làm việc</div>
        <h1 className="font-serif text-2xl font-bold tracking-tight">Đăng ký ca</h1>
        <div className="mt-3 flex items-center justify-between">
          <button onClick={() => shiftWeek(-1)} className="p-2 bg-white/10 rounded-sm" data-testid="btn-prev-week"><ChevronLeft className="w-4 h-4" /></button>
          <div className="text-sm font-semibold text-center">
            <CalendarDays className="w-4 h-4 inline mr-1" />
            {weekStart.toLocaleDateString("vi-VN", { day: "2-digit", month: "2-digit" })} – {new Date(weekStart.getTime() + 6 * 86400000).toLocaleDateString("vi-VN", { day: "2-digit", month: "2-digit", year: "numeric" })}
          </div>
          <button onClick={() => shiftWeek(1)} className="p-2 bg-white/10 rounded-sm" data-testid="btn-next-week"><ChevronRight className="w-4 h-4" /></button>
        </div>
        <div className="mt-3">
          <Select value={branchFilter} onValueChange={setBranchFilter}>
            <SelectTrigger className="h-9 bg-white/10 border-white/20 text-white rounded-sm text-sm" data-testid="sel-branch"><SelectValue placeholder="Chọn chi nhánh" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Tất cả chi nhánh</SelectItem>
              {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="p-3 space-y-3">
        {data.days?.length === 0 && <div className="text-center text-stone-400 py-10 text-sm">Đang tải…</div>}
        {data.days?.map((d) => {
          const dateObj = new Date(d.date);
          const isToday = isoDate(new Date()) === d.date;
          const wdName = VN_DAYS[d.weekday];
          return (
            <div key={d.date} className={`bg-white rounded-sm border ${isToday ? "border-emerald-700" : "border-stone-200"} overflow-hidden`} data-testid={`day-${d.date}`}>
              <div className={`px-3 py-2 flex items-center justify-between ${isToday ? "bg-emerald-50" : "bg-stone-50"} border-b border-stone-200`}>
                <div>
                  <span className="font-serif font-bold text-stone-900">{wdName}</span>
                  <span className="text-xs text-stone-500 ml-2 font-mono">{dateObj.toLocaleDateString("vi-VN", { day: "2-digit", month: "2-digit" })}</span>
                  {isToday && <span className="ml-2 text-[10px] px-1.5 py-0.5 bg-emerald-900 text-white rounded-sm font-bold">HÔM NAY</span>}
                </div>
                {d.day_override && (
                  <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-900 rounded-sm font-semibold">
                    ×{Number(d.day_override.pay_multiplier)}%{d.day_override.reason ? ` · ${d.day_override.reason}` : ""}
                  </span>
                )}
              </div>
              <div className="divide-y divide-stone-100">
                {d.shifts.length === 0 && <div className="px-3 py-4 text-center text-stone-400 text-xs">Không có ca</div>}
                {d.shifts.map((s) => {
                  const full = s.slots_available === 0 && !s.my_registration_id;
                  const mine = !!s.my_registration_id;
                  const k = `${s.template_id}_${d.date}`;
                  return (
                    <div key={s.template_id} className="px-3 py-2.5 flex items-center gap-3" data-testid={`shift-${s.template_id}-${d.date}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="font-semibold text-stone-900">{s.name}</div>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-sm font-semibold ${
                            s.effective_multiplier >= 200 ? "bg-amber-100 text-amber-900" :
                            s.effective_multiplier >= 150 ? "bg-emerald-100 text-emerald-900" :
                            "bg-stone-100 text-stone-700"}`}>×{s.effective_multiplier}%</span>
                        </div>
                        <div className="text-xs text-stone-500 mt-0.5 flex items-center gap-2 flex-wrap">
                          <span className="inline-flex items-center gap-0.5"><Clock className="w-3 h-3" />{s.start_time}–{s.end_time}</span>
                          <span className="inline-flex items-center gap-0.5"><MapPin className="w-3 h-3" />{s.branch_name}</span>
                          <span className="inline-flex items-center gap-0.5"><Users className="w-3 h-3" />{s.slots_taken}/{s.max_slots}</span>
                        </div>
                      </div>
                      {mine ? (
                        <Button
                          onClick={() => cancelMine(s.my_registration_id)}
                          disabled={busy === s.my_registration_id}
                          size="sm"
                          variant="outline"
                          className="rounded-sm h-8 text-red-700 border-red-200"
                          data-testid={`btn-cancel-${k}`}
                        >
                          <X className="w-3 h-3 mr-1" />Hủy
                        </Button>
                      ) : full ? (
                        <span className="text-[10px] px-2 py-1 bg-stone-100 text-stone-500 rounded-sm font-bold">HẾT SLOT</span>
                      ) : (
                        <Button
                          onClick={() => register(s.template_id, d.date)}
                          disabled={busy === k}
                          size="sm"
                          className="rounded-sm h-8 bg-emerald-900 text-white"
                          data-testid={`btn-register-${k}`}
                        >
                          <Check className="w-3 h-3 mr-1" />Đăng ký
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
